import React, { useContext, memo, useEffect, useRef} from "react";
import { ContextEngine } from "../../engine/ContextEngine";
import "./Left.css";
import { useNamespaces } from "../../engine/hooks/useNamespaces";


const Left = memo(() => {
  const scrollContainerRef = useRef(null); // Create the ref
  const { context, updateContext } = useContext(ContextEngine);
  const visible = context.panelVisibility?.left ?? true;
  const activeNamespace = context.view?.activeNamespace;
    // Grab the ID from the shared context
  const selectedProjectId = context.view?.selectedProject;
  const selectedSection= context.view?.selectedSection;

  // Fetch only what this panel needs
  const { 
    data: namespaces = [], 
    isLoading, 
    error 
  } = useNamespaces(selectedProjectId,"build");

  const getDynamicStyle = (index, total, isActive) => {
    const hue = (index * (360 / Math.max(total, 1))) % 360;
    const baseColor = `hsl(${hue}, 70%, 60%)`;
    return {
      backgroundColor: isActive ? `hsla(${hue}, 70%, 60%, 0.15)` : "transparent",
      borderLeft: isActive ? `3px solid ${baseColor}` : "3px solid transparent",
      color: isActive ? "#ffffff" : "var(--text-dim)",
      iconColor: baseColor,
    };
  };
  const ICON_MAP = {
    namespaces: "💎",
    sheets: "📊",
    events: "⚡",
    controls: "🕹️",
    agents: "🤖",
    actions: "🚀",
    projects: "🏗️", // or 📁
    users: "👨‍💻",    // or 👨‍💻
    teams: "🤝"     // or 🤝
  };

  const handleNamespaceChange = (cont) => {
    if (!cont) return; // Guard clause
    updateContext("view", {
      ...context.view,
      activeNamespace: cont,
      page: 1,
      filters: {},
      search: ""
    });
  };

  // 3. Auto-select first item if current is invalid
  useEffect(() => {
    if (!isLoading && namespaces.length > 0) {
      // Use ?. to prevent crashing on first load
      const currentId = activeNamespace?.row_id;
      const isValid = namespaces.some(ns => ns.row_id === currentId);
      
      if (!isValid) {
        handleNamespaceChange(namespaces[0]);
      }
    }
  }, [namespaces, activeNamespace?.row_id, isLoading]); // Only re-run if the ID changes


  // 4. Keyboard Navigation (Fixed to use row_id)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement.tagName;
      if (!visible || namespaces.length === 0 || /INPUT|SELECT|TEXTAREA/.test(activeTag)) return;
      
      // FIX: Changed .id to .row_id
      const currentIndex = namespaces.findIndex(c => c.row_id === activeNamespace?.row_id);
      
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % namespaces.length;
        handleNamespaceChange(namespaces[nextIndex]);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + namespaces.length) % namespaces.length;
        handleNamespaceChange(namespaces[prevIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeNamespace, namespaces, visible]); 

  if (!visible) return null;

  return (
    <div className="left-panel">
      {/* HEADER SECTION: Title, Settings, and Close grouped at the top */}
      <div className="panel-header">
        <span className="panel-title">WORKSPACE</span>
        <div className="header-actions">
          <button 
            className="btn-icon" 
            title="Settings"
            onClick={() => updateContext("view.modal", { type: "SETTINGS" })}
          >
            ⚙️
          </button>
          <button 
            className="btn-icon btn-close"
            onClick={() => updateContext("panelVisibility.left", false)}
          >
            «
          </button>
        </div>
      </div>

      <div className="nav-section">
        <div className="section-label">SHEETS</div>
        <div className="namespace-list" ref={scrollContainerRef}>
        {namespaces.map((c, index) => { 
            // Use c.id or c.row_id depending on your to_dict() output
            const isActive = activeNamespace?.row_id === c.row_id; 
            const dynamicStyle = getDynamicStyle(index, namespaces.length, isActive);
            const icon = ICON_MAP[c.cont_value] || "📁";
            return (
              <div
                key={c.row_id}
                onClick={() => handleNamespaceChange(c)}
                className={`namespace-item ${isActive ? "active" : ""}`}
                style={dynamicStyle}
              >
                <span 
                  className="namespace-icon" 
                  style={{ color: dynamicStyle.iconColor, marginRight: "10px" }}
                >
                  {icon}
                </span>
                <span className="namespace-label">{c.cont_value}</span>
              </div>
            );
        })}
        </div>
      </div>
    </div>
  );
});

export default Left;
